import { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Dialog, DialogContent,
  DialogTitle, DialogActions, IconButton, Chip, TextField, Tooltip,
  CircularProgress, InputAdornment, Avatar, Menu, MenuItem, Grid
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Play, Building, Share2, Clock, Users, Link as LinkIcon, Copy } from 'lucide-react';
import {
  Delete as DeleteIcon, Info as InfoIcon, Close as CloseIcon,
  Search as SearchIcon, MoreVert as MoreVertIcon, AccessTime as AccessTimeIcon,
  Person as PersonIcon, Business as BusinessIcon
} from '@mui/icons-material';
import apiInstance from 'src/utils/apiAuth';
import { showErrorToast, showToast } from 'src/utils/Toast/SnackbarProvider';
import { getDDMMYYYYFormat } from 'src/utils/commonUtils';
import { createVideoThumbnail } from 'src/utils/videoThumbnailUtil';
import * as constants from 'src/utils/constants';

// Shared Video Card Component
function SharedVideoCard({ sharedVideo, onRemove, onViewInfo }) {
  const [open, setOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);

  const document = sharedVideo.document || {};
  const company = sharedVideo.company || {};
  const sharedBy = sharedVideo.shared_by || {};
  
  const fileUrl = document.file || '';
  const fileTitle = document.file_name || 'Video';
  const isExpired = sharedVideo.is_expired;
  const expiresAt = sharedVideo.expires_at ? new Date(sharedVideo.expires_at) : null;

  useEffect(() => {
    const generate = async () => {
      try {
        if (!fileUrl) return;
        const src = fileUrl.startsWith('http') 
          ? fileUrl 
          : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${fileUrl}`;
        const img = await createVideoThumbnail(src);
        if (img) setThumbnail(img);
      } catch (e) {
        console.error('Thumbnail generation failed:', e.message);
      }
    };
    generate();
  }, [fileUrl]);

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  };

  const handleMenuClose = (e) => {
    if (e) e.stopPropagation();
    setMenuAnchorEl(null);
  };

  const getDaysRemaining = () => {
    if (!expiresAt) return null;
    const now = new Date();
    const diff = expiresAt - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days;
  };

  const daysRemaining = getDaysRemaining();

  return (
    <>
      <Card
        onClick={() => !isExpired && setOpen(true)}
        sx={{
          cursor: isExpired ? 'not-allowed' : 'pointer',
          opacity: isExpired ? 0.6 : 1,
          '&:hover': {
            boxShadow: isExpired ? 'none' : '0 4px 12px rgba(0,0,0,0.15)'
          },
          borderRadius: 3,
          overflow: 'hidden',
          height: '100%',
          border: isExpired ? '2px solid #f44336' : 'none'
        }}
      >
        <Box sx={{
          height: 140,
          backgroundColor: '#000',
          backgroundImage: thumbnail ? `url(${thumbnail})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}>
          {!thumbnail && (
            <video
              src={fileUrl.startsWith('http') ? fileUrl : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${fileUrl}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                position: 'absolute',
                top: 0,
                left: 0
              }}
              muted
              playsInline
              preload="metadata"
            />
          )}
          <Play size={40} color="rgba(255,255,255,0.9)" style={{ 
            position: 'relative', 
            zIndex: 1, 
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' 
          }} />
          
          {/* Status chip */}
          {isExpired ? (
            <Chip
              label="Expired"
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: '#f44336',
                color: 'white',
                fontSize: '0.65rem',
                height: 22
              }}
            />
          ) : daysRemaining !== null && daysRemaining <= 7 && (
            <Chip
              icon={<Clock size={12} />}
              label={`${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} left`}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: daysRemaining <= 3 ? '#ff9800' : '#4caf50',
                color: 'white',
                fontSize: '0.65rem',
                height: 22,
                '& .MuiChip-icon': { fontSize: 12, color: 'white' }
              }}
            />
          )}

          {/* 3-dot menu button */}
          <IconButton
            onClick={handleMenuOpen}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { bgcolor: '#e3f2fd' },
              width: 28,
              height: 28
            }}
          >
            <MoreVertIcon sx={{ fontSize: 16, color: '#64748b' }} />
          </IconButton>
        </Box>

        <CardContent sx={{ p: 1.5 }}>
          <Tooltip title={fileTitle} arrow>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.8rem'
              }}
            >
              {fileTitle}
            </Typography>
          </Tooltip>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Building size={14} color="#64748b" />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
              {company.company_name || 'Unknown Company'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Avatar 
              sx={{ width: 16, height: 16, fontSize: '0.5rem', bgcolor: '#2e7d32' }}
            >
              {sharedBy.first_name?.[0] || 'U'}
            </Avatar>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
              Shared by {sharedBy.first_name} {sharedBy.last_name}
            </Typography>
          </Box>
          
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
            Expires: {expiresAt ? getDDMMYYYYFormat(sharedVideo.expires_at) : 'N/A'}
          </Typography>
        </CardContent>
      </Card>

      {/* 3-dot Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={(e) => { handleMenuClose(e); onViewInfo && onViewInfo(sharedVideo); }}>
          <InfoIcon sx={{ mr: 1, fontSize: 18, color: '#1976d2' }} />
          <Typography color="#1976d2">View Details</Typography>
        </MenuItem>
        <MenuItem onClick={(e) => { handleMenuClose(e); onRemove && onRemove(sharedVideo); }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 18, color: '#d32f2f' }} />
          <Typography color="#d32f2f">Remove Share</Typography>
        </MenuItem>
      </Menu>

      {/* Video Preview Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{fileTitle}</Typography>
          <IconButton onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <video
            controls
            style={{ width: '100%', height: 'auto', maxHeight: '500px' }}
            src={fileUrl.startsWith('http') ? fileUrl : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${fileUrl}`}
          >
            Your browser does not support the video tag.
          </video>
          
          {sharedVideo.notes && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2, textAlign: 'left' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Notes</Typography>
              <Typography variant="body2">{sharedVideo.notes}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// Main Shared Videos Page
export default function SharedVideos() {
  const [sharedVideos, setSharedVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);

  const fetchSharedVideos = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('my_videos', 'true');
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      const response = await apiInstance.get(`shared-videos/?${params.toString()}`, {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });
      
      const data = response.data?.results?.data || response.data?.data || response.data?.results || response.data || [];
      console.log('Shared videos response:', response.data);
      console.log('Extracted data:', data);
      setSharedVideos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching shared videos:', error);
      showErrorToast('Failed to load shared videos');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSharedVideos();
  }, []);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      fetchSharedVideos();
    }, 500);
    return () => clearTimeout(delaySearch);
  }, [searchQuery]);

  // Handle remove
  const handleRemove = (item) => {
    setDeletingItem(item);
    setDeleteDialogOpen(true);
  };

  const confirmRemove = async () => {
    if (!deletingItem) return;
    
    setIsDeleting(true);
    try {
      await apiInstance.delete(`shared-videos/${deletingItem.id}/`, {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });
      
      showToast('Share removed successfully', 'success');
      setDeleteDialogOpen(false);
      fetchSharedVideos();
    } catch (error) {
      showErrorToast('Failed to remove share');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle view info
  const handleViewInfo = (item) => {
    setViewingItem(item);
    setInfoDialogOpen(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard', 'success');
  };

  const activeVideos = sharedVideos.filter(v => !v.is_expired);
  const expiredVideos = sharedVideos.filter(v => v.is_expired);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Share2 size={32} color="#2e7d32" />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Shared Videos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Videos shared with you and by you - {activeVideos.length} active, {expiredVideos.length} expired
            </Typography>
          </Box>
        </Box>
        
        {/* Search */}
        <TextField
          placeholder="Search videos, companies..."
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            )
          }}
          sx={{ width: 300 }}
        />
      </Box>

      {/* Loading */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : sharedVideos.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Share2 size={64} color="#9e9e9e" style={{ marginBottom: 16 }} />
          <Typography variant="h6" color="text.secondary">
            No shared videos yet
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Videos shared with you and by you
          </Typography>
        </Box>
      ) : (
        <>
          {/* Active Videos */}
          {activeVideos.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2e7d32' }}>
                Active ({activeVideos.length})
              </Typography>
              <Grid container spacing={2}>
                {activeVideos.map((video) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
                    <SharedVideoCard
                      sharedVideo={video}
                      onRemove={handleRemove}
                      onViewInfo={handleViewInfo}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Expired Videos */}
          {expiredVideos.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#f44336' }}>
                Expired ({expiredVideos.length})
              </Typography>
              <Grid container spacing={2}>
                {expiredVideos.map((video) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
                    <SharedVideoCard
                      sharedVideo={video}
                      onRemove={handleRemove}
                      onViewInfo={handleViewInfo}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remove Share?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this shared video? This will revoke access for all recipients.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <LoadingButton 
            onClick={confirmRemove}
            loading={isDeleting}
            color="error"
            variant="contained"
          >
            Remove
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Info Dialog */}
      <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Share Details</Typography>
          <IconButton onClick={() => setInfoDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {viewingItem && (
            <Box>
              <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {viewingItem.document?.file_name || 'Video'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {viewingItem.company?.company_name || 'Unknown Company'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  <PersonIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  Shared By
                </Typography>
                <Typography variant="body2">
                  {viewingItem.shared_by?.first_name} {viewingItem.shared_by?.last_name}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  Expiry
                </Typography>
                <Typography variant="body2">
                  {viewingItem.expires_at ? new Date(viewingItem.expires_at).toLocaleString() : 'N/A'}
                  {viewingItem.is_expired && (
                    <Chip label="Expired" size="small" color="error" sx={{ ml: 1 }} />
                  )}
                </Typography>
              </Box>

              {viewingItem.internal_users?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    <Users size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    Internal Recipients ({viewingItem.internal_users.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {viewingItem.internal_users.map((user, idx) => (
                      <Chip 
                        key={idx} 
                        label={`${user.first_name} ${user.last_name}`} 
                        size="small" 
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {viewingItem.external_members?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    <Users size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    External Members ({viewingItem.external_members.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {viewingItem.external_members.map((member, idx) => (
                      <Chip 
                        key={idx} 
                        label={`${member.first_name} ${member.last_name || ''}`.trim() || member.email || 'Contact'} 
                        size="small" 
                        variant="outlined"
                        color="warning"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {viewingItem.additional_emails?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    <Users size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    Additional Email Recipients ({viewingItem.additional_emails.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {viewingItem.additional_emails.map((email, idx) => (
                      <Chip 
                        key={idx} 
                        label={email} 
                        size="small" 
                        variant="outlined"
                        color="secondary"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {viewingItem.public_url && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    <LinkIcon size={16} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    Public Link & Access Code
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <TextField
                        size="small"
                        value={viewingItem.public_url}
                        InputProps={{ readOnly: true }}
                        fullWidth
                      />
                      <IconButton 
                        onClick={() => copyToClipboard(viewingItem.public_url)}
                        sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}
                        title="Copy Link"
                      >
                        <Copy size={18} />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="body2">
                        Access Code: <strong style={{ fontSize: '1.1rem', letterSpacing: '2px' }}>{viewingItem.access_code}</strong>
                      </Typography>
                      <IconButton 
                        onClick={() => copyToClipboard(viewingItem.access_code)}
                        sx={{ bgcolor: '#e8f5e9', '&:hover': { bgcolor: '#c8e6c9' } }}
                        title="Copy Code"
                      >
                        <Copy size={18} />
                      </IconButton>
                    </Box>
                    <Button 
                      variant="contained" 
                      size="small"
                      startIcon={<Copy size={16} />}
                      onClick={() => copyToClipboard(`Link: ${viewingItem.public_url}\nAccess Code: ${viewingItem.access_code}`)}
                      sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                    >
                      Copy Link & Code
                    </Button>
                  </Box>
                </Box>
              )}

              {viewingItem.notes && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Notes</Typography>
                  <Typography variant="body2" sx={{ p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                    {viewingItem.notes}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
